// The call's AudioContext, one per session: the cues, the speaking levels and
// noise suppression share it.
//
// Promise:
// - Made at 48 kHz (DeepFilterNet works at that rate; the browser converts a
//   44.1 kHz device).
// - `ensure()` makes it, and asks a suspended one to resume. The session calls
//   it in the click that joins, before anything is awaited: made later, Safari
//   can keep it suspended, and then even the voice sent through noise
//   suppression is silent.
// - While a call is on (`watch`), a suspended or interrupted context (iOS: a
//   phone call, Siri, the page sent to the back) is asked to resume when its
//   state changes, when the page shows again, and on the next touch.
// - Without AudioContext (tests, old browsers) it is null and nothing plays.

export interface CallAudio {
  ensure(): AudioContext | null;
  /** Keeps the context running while the call is on; the returned function stops watching. */
  watch(): () => void;
}

export function createCallAudio(
  make: () => AudioContext | null = () =>
    typeof AudioContext === "undefined"
      ? null
      : new AudioContext({ sampleRate: 48000 }),
): CallAudio {
  let ctx: AudioContext | null = null;
  const wake = () => {
    // "interrupted" is Safari's state for a context another sound took over.
    if (ctx && (ctx.state as string) !== "running")
      void ctx.resume().catch(() => {});
  };
  return {
    ensure() {
      ctx ??= make();
      wake();
      return ctx;
    },
    watch() {
      const onVisible = () => {
        if (document.visibilityState === "visible") wake();
      };
      ctx?.addEventListener("statechange", wake);
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("pointerdown", wake, true);
      return () => {
        ctx?.removeEventListener("statechange", wake);
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("pointerdown", wake, true);
      };
    },
  };
}
